// @ts-check
// Cliente de servicio de progreso por bloque (MOD-FRONTEND-CORE, TSK-0115,
// CAP-PROGRESS-EXERCISE-SELECTOR, docs/03-frontend.md §9.1). Como todo
// servicio, no toca el DOM y usa exclusivamente `apiRequest` de
// `api-client.js` -- nunca `fetch` directamente. Reutiliza
// `mapearEjercicioPublico` de `exercise-client.js` para el subcampo
// `ejercicio` de cada elemento de la lista; no redefine ese mapeo aquí.
//
// Contrato real verificado: `GET /api/v1/progress/blocks/{bloque}/exercises`
// (backend Ola 4, en producción), {bloque} ∈ {diagrama, pseudocodigo,
// python}. Requiere sesión autenticada (401 sin sesión, propagado como
// `ApiError` con kind 'unauthorized' -- ver `classifyStatus` en
// `api-errors.js`). Responde `list[EjercicioConEstado]`, cada ítem en
// snake_case: `{ejercicio: <EjercicioPublico>, bloque, orden: int,
// estado: "bloqueado"|"desbloqueado"|"completado"|"no_superado",
// nivel: "inicial"|"intermedio"|"avanzado"}`.
//
// DEC-037/DEC-123: el estado ("bloqueado"/"desbloqueado"/"completado"/
// "no_superado" -- este último añadido cuando el alumno agota los intentos
// que puntúan sin completar el ejercicio, 2026-09-12) ya viene derivado por
// el servidor; este cliente SOLO lo refleja, nunca lo recalcula ni lo
// infiere localmente (SEC-R4 -- la lógica de desbloqueo es responsabilidad
// exclusiva del backend). Por eso la normalización defensiva de un valor de
// `estado` desconocido/corrupto cae siempre a 'bloqueado': el fallo seguro
// es no desbloquear nunca por un dato corrupto o inesperado, jamás al revés.
//
// `ESTADOS_VALIDOS` (más abajo) tiene que seguir coincidiendo con
// `backend/src/app/schemas/progress.py::EstadoEjercicio`: lo vigila
// `tests/unit/services/progress-client-estados.spec.js` (guardián, lee el
// fichero fuente del backend).
//
// DEC-016: `EjercicioPublico` (backend/src/app/schemas/exercise.py) ya
// excluye los campos ocultos de docente (rúbrica, contenido_docente,
// solución) en la proyección pública; `mapearEjercicioPublico` ya refleja
// esa exclusión, así que no hay nada adicional que ocultar aquí.
//
// `credentials: 'include'`: mismo valor que usa `auth-client.js` en todas
// sus funciones (`iniciarSesion`, `obtenerUsuarioActual`, etc.). Este
// endpoint también requiere la cookie de sesión HttpOnly; `apiRequest` usa
// 'same-origin' por defecto, insuficiente si el proxy de dev sirve la API
// en otro origen -- sin 'include' la cookie no viajaría y la petición
// fallaría siempre con 401 aunque hubiera una sesión válida.

import { apiRequest } from './api-client.js';
import { mapearEjercicioPublico } from './exercise-client.js';
import { BLOQUES, esBloque } from '../utils/bloques.js';

/** @typedef {'bloqueado'|'desbloqueado'|'completado'|'no_superado'} EstadoEjercicio */

/**
 * Alias del tipo compartido (`utils/bloques.js`). Se conserva el nombre local
 * porque otros módulos ya lo referencian como
 * `import('./progress-client.js').BloqueProgreso`.
 * @typedef {import('../utils/bloques.js').Bloque} BloqueProgreso
 */

/** @typedef {'inicial'|'intermedio'|'avanzado'} NivelProgreso */

/**
 * `nivel` se declara opcional (`?`) en el typedef -- aunque
 * `mapearEjercicioConEstado` SIEMPRE lo puebla en runtime (ver
 * `normalizarNivel`, nunca deja el campo `undefined`) -- únicamente para no
 * romper, en tiempo de tipos, los fixtures de prueba ya existentes de otros
 * módulos que construyen objetos `EjercicioConEstado` sin `nivel` (p. ej.
 * `tests/unit/components/exercise-selector.spec.js`, fuera de las
 * `rutas_permitidas` de TSK-0146). Contradicción detectada y documentada en
 * el `historial` de la ficha: el criterio de aceptación exige que `tsc
 * --noEmit` pase sin tocar archivos fuera de alcance; declarar `nivel`
 * como no-opcional rompía ese `tsc` en un archivo prohibido. Marcarlo
 * opcional resuelve el conflicto sin ampliar el alcance. Se recomienda una
 * tarea de seguimiento para actualizar esos fixtures y, si procede,
 * endurecer `nivel` a obligatorio.
 * @typedef {{
 *   ejercicio: import('./exercise-client.js').EjercicioPublico,
 *   bloque: BloqueProgreso,
 *   orden: number,
 *   estado: EstadoEjercicio,
 *   nivel?: NivelProgreso,
 *   experienciaRetiradaMotivo?: string|null,
 * }} EjercicioConEstado
 */

// Exportado SOLO para el guardián de drift
// (`tests/unit/services/progress-client-estados.spec.js`), que lo compara
// con `backend/src/app/schemas/progress.py::EstadoEjercicio`. Ningún otro
// módulo debería importarlo: la normalización vive en `normalizarEstado`.
export const ESTADOS_VALIDOS = /** @type {const} */ ([
  'bloqueado',
  'desbloqueado',
  'completado',
  'no_superado',
]);

/**
 * ¿El ejercicio ya no suma puntos? Lo está si se completó y también si se
 * gastaron los intentos que puntuaban ('no_superado', 2026-09-12). En los dos
 * casos el panel de corrección solo ofrece "Practicar". Lo usan las tres
 * páginas de editor: la regla vive una vez.
 * @param {unknown} estado
 * @returns {boolean}
 */
export function soloSePractica(estado) {
  return estado === 'completado' || estado === 'no_superado';
}

const NIVELES_VALIDOS = /** @type {const} */ (['inicial', 'intermedio', 'avanzado']);

/**
 * @param {unknown} valor
 * @returns {valor is BloqueProgreso}
 */
function esBloqueValido(valor) {
  return esBloque(valor);
}

/**
 * Normaliza un valor de `estado` crudo a uno de los cuatro valores válidos
 * (`ESTADOS_VALIDOS`). Deliberadamente conservador (SEC-R4/DEC-037): cualquier valor
 * desconocido, corrupto o ausente se trata como 'bloqueado', nunca como
 * 'desbloqueado' ni 'completado' -- un ejercicio nunca se auto-desbloquea
 * por un dato inesperado.
 * @param {unknown} valor
 * @returns {EstadoEjercicio}
 */
function normalizarEstado(valor) {
  return ESTADOS_VALIDOS.includes(/** @type {EstadoEjercicio} */ (valor))
    ? /** @type {EstadoEjercicio} */ (valor)
    : 'bloqueado';
}

/**
 * Normaliza un valor de `nivel` crudo a uno de los tres valores válidos.
 * A diferencia de `normalizarEstado`, este fallback NO es un fail-closed de
 * seguridad: `nivel` es un metadato de secuencia/agrupación de presentación
 * (rango_de_nivel usado por el backend para ordenar, DEC-059/DEC-060), no
 * gobierna el desbloqueo de ejercicios (eso lo decide exclusivamente
 * `estado`, ver `normalizarEstado`). Por eso un valor ausente, desconocido
 * o corrupto cae de forma determinista a 'inicial' -- el nivel más bajo --
 * simplemente para tener un valor consistente de agrupación, sin ninguna
 * implicación de seguridad.
 * @param {unknown} valor
 * @returns {NivelProgreso}
 */
function normalizarNivel(valor) {
  return NIVELES_VALIDOS.includes(/** @type {NivelProgreso} */ (valor))
    ? /** @type {NivelProgreso} */ (valor)
    : 'inicial';
}

/**
 * Mapea un ítem crudo de la respuesta (snake_case) a `EjercicioConEstado`
 * (camelCase donde aplica). Defensivo con `unknown`, igual criterio que
 * `mapearEjercicioPublico` en `exercise-client.js`.
 * @param {unknown} item
 * @param {BloqueProgreso} bloqueSolicitado
 * @returns {EjercicioConEstado}
 */
function mapearEjercicioConEstado(item, bloqueSolicitado) {
  const crudo = /** @type {Record<string, unknown>} */ (item ?? {});
  return {
    ejercicio: mapearEjercicioPublico(crudo.ejercicio),
    bloque: esBloqueValido(crudo.bloque) ? crudo.bloque : bloqueSolicitado,
    orden: typeof crudo.orden === 'number' ? crudo.orden : 0,
    estado: normalizarEstado(crudo.estado),
    nivel: normalizarNivel(crudo.nivel),
    // Si el profesor le ha retirado la experiencia de este ejercicio (DEC-182),
    // el motivo que escribió; si no, `null`.
    experienciaRetiradaMotivo:
      typeof crudo.experiencia_retirada_motivo === 'string' &&
      crudo.experiencia_retirada_motivo !== ''
        ? crudo.experiencia_retirada_motivo
        : null,
  };
}

/**
 * Obtiene la lista de ejercicios de un bloque junto a su estado de
 * progreso para el usuario autenticado
 * (`GET /api/v1/progress/blocks/{bloque}/exercises`). Requiere sesión
 * (propaga `ApiError` con kind 'unauthorized' si no la hay). El estado
 * devuelto ya viene derivado íntegramente por el servidor (DEC-037); esta
 * función solo lo refleja de forma defensiva, nunca lo recalcula.
 * Propaga `ApiError` sin capturarla (incluida 'unauthorized'): esta capa
 * de servicio no decide cómo se muestra el error, eso es responsabilidad
 * de la página que la llama (mismo criterio que `obtenerEjercicioPorSlug`).
 * @param {BloqueProgreso} bloque
 * @param {{ signal?: AbortSignal }} [opciones]
 * @returns {Promise<EjercicioConEstado[]>}
 */
export async function obtenerEjerciciosDeBloque(bloque, opciones = {}) {
  if (!esBloqueValido(bloque)) {
    // La lista del mensaje se deriva de `BLOQUES`: enumerarla a mano aquí era
    // otra copia más que podía quedarse desfasada justo cuando más importa,
    // en el texto que lee quien está depurando el fallo.
    throw new TypeError(
      `obtenerEjerciciosDeBloque: "bloque" debe ser uno de ${BLOQUES.map((b) => `"${b}"`).join(', ')}`,
    );
  }

  const datos = await apiRequest(`/progress/blocks/${encodeURIComponent(bloque)}/exercises`, {
    signal: opciones.signal,
    credentials: 'include',
  });

  if (!Array.isArray(datos)) {
    return [];
  }

  return datos.map((item) => mapearEjercicioConEstado(item, bloque));
}
